import ArrowDropDownRounded from "@mui/icons-material/ArrowDropDownRounded";
import MenuFilled from "@mui/icons-material/Menu";
import { useMediaQuery } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { IconButton } from "component/MarketPlace/ButtonComponents";
import React, { memo, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import MultiLevelDropDown from "../multiLevelDropDown/multiLevelDropDown";
import "./../../../styles/scss/common.scss";
import GlobalSearch, { IGlobalSearchProps } from "./globalSearch/globalSearch";
import { GroupMenuItem, StyledTopNav, StyledTopNavWrapper, TopNavWrapperContainer } from "./topNav.style";
import { ActiveKeys, MenuItem } from "./topNav.types";
import { getAllUrls } from "./topNav.utils";
import { Home } from "@mui/icons-material";
import { ThemeContext } from "styled-components";
import { createPseudoStyleFromMuiIcon, generateImgUrl } from "utils/functions";
import UptimeImage from "component/commonComponents/UptimeImage/UptimeImage";

export interface TopNavProps extends PropsWithChildren {
  logoImg?: string;
  menuItems: MenuItem[];
  onClickBtnMenu: (item: any, activeLevel?: string) => void;
  onClickBtnLogo: () => void;
  onItemClick: (item: any) => void;
  location: any;
  configs?: {
    sticky?: boolean;
    homeIcon?: boolean
  };
  globalSearchProps?: IGlobalSearchProps;
  userData: {
    lockedOut?: boolean;
    [key: string]: any;
  };
  handleOpenSubmenu: (isOpen: boolean) => void;
  portalName?: string;
}

export const PORTAL_CONFIG = 'PORTAL_CONFIG';
export const HIDE_TOP_NAV_OPTIONS = {
  TRUE: 'true',
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
};

const MENU_TYPE = {
  LOGO: 'logo',
  MENU_ITEM: 'menu-item',
};

const TopNav = (props: TopNavProps) => {
  const {
    logoImg,
    menuItems,
    configs = {},
    children,
    onClickBtnMenu,
    onClickBtnLogo,
    onItemClick,
    location,
    globalSearchProps = {},
    userData,
    handleOpenSubmenu,
    portalName,
  } = props;
  const { sticky, homeIcon } = configs;

  const theme  = useContext(ThemeContext);
  const lgUp = useMediaQuery(`(min-width:${theme.breakpoints.lg})`);

  const [expanded, setExpanded] = useState<string>("");
  const [activeKeys, setActiveKeys] = useState<ActiveKeys>(["", ""]);
  const [activeLv1, activeLv2] = activeKeys;
  const isStickyMenu = activeKeys.filter(Boolean).length > 0 ? sticky : false;

  const selectedSubMenuItems = useMemo(() => {
    const expandedKey = expanded || activeLv1;
    if (!expandedKey) return [];

    const selectedItem = menuItems.find((item) => item.key === expandedKey);
    return selectedItem?.items || [];
  }, [expanded, activeLv1, menuItems]);

  const menuUrls = useMemo(() => getAllUrls(menuItems), [menuItems]);

  const { hideTopNav } = JSON.parse(sessionStorage.getItem(PORTAL_CONFIG) || '{}');

  useEffect(() => {
    let activeKeys: ActiveKeys | undefined;
    let temp: ActiveKeys = ['', ''];
    for (const [keyLv1, urlsLv1] of Object.entries(menuUrls)) {
      for (const [keyLv2, urlsLv2] of Object.entries(urlsLv1)) {
        // eslint-disable-next-line no-loop-func
        const index = urlsLv2.findIndex((url) => {
          const pathname = url.includes('?')
            ? `${location.pathname}${location.search}`
            : location.pathname;
          if (pathname !== url && pathname.startsWith(url)) {
            temp = [keyLv1, keyLv2];
          }
          return pathname === url;
        });

        if (index > -1) {
          activeKeys = [keyLv1, keyLv2];
          break;
        }
      }
      if (activeKeys) break;
    }
    setActiveKeys(activeKeys || temp);
    setExpanded(activeKeys?.[0] || temp?.[0]);
  }, [location, menuUrls]);

  const handleChange = (item: MenuItem) => {
    setExpanded(expanded === item.key ? "" : item.key);
  };

  const handleClickAway = () => {
    setExpanded("");
  };

  const enterKeyPress = (e: { keyCode: any;}, item: any, activeLevel: any, menuType: string) => {
    if (e.keyCode === 13) {
      switch (menuType) {
        case MENU_TYPE.MENU_ITEM:
          onClickBtnMenu({ ...item }, activeLevel);
          handleChange(item);
          break;
        case MENU_TYPE.LOGO:
          onClickBtnLogo();
          break
        default:
          break;
      }
    }
  };

  useEffect(() => {
   handleOpenSubmenu(!!activeLv1);
  },[activeLv1]);

  const listMenu = (menuItems: MenuItem[]) => {
    const menu = menuItems.map((item, index) => {
      const showMoreIcon = item.key === activeLv1 || !item.hasLandingPage;
      const isExpanded = expanded === item.key || (activeLv1 === item.key && !expanded);
      const icon = showMoreIcon ? <ArrowDropDownRounded /> : null;

      return (
            <li key={index}>
              <a
                href={item.url || '#'}
                onClick={(e) => {
                  e.preventDefault();
                  onClickBtnMenu({ ...item }, activeLv1);
                }}
                className={`${isExpanded ? "expanded" : ""} title-no-sub group-menu-header`}
                tabIndex={0}
                onKeyDown={(e)=> enterKeyPress(e, item, activeLv1, MENU_TYPE.MENU_ITEM)}
              >
                <GroupMenuItem
                  expanded={isStickyMenu ? isExpanded : expanded === item.key}
                  onClick={() => handleChange(item)}
                  pseudoStyle={createPseudoStyleFromMuiIcon(icon)}
                >
                  {item.name}
                </GroupMenuItem>
              </a>
                {lgUp && isExpanded && (
                  <MultiLevelDropDown
                    sticky={isStickyMenu}
                    active={activeLv2}
                    onMultiLevelMenuClick={onItemClick}
                    isOpen={isStickyMenu || expanded === item.key}
                    menuItems={selectedSubMenuItems}
                    setExpanded={setExpanded}
                    location={location.pathname}
                    hidden={hideTopNav === HIDE_TOP_NAV_OPTIONS.SECONDARY}
                  />
                )}
            </li>
          );
    });

    return menu;
  };

  const menuHasItems = useMemo(() => {
    const activeMenu = menuItems.find((menu) => menu.key === activeLv1);
    if (!activeMenu) return false;

    const itemsLength = activeMenu.items?.length || 0;
    return itemsLength > 0;
  }, [menuItems, activeLv1]);

  return (
    <StyledTopNav isSubmenu={!!activeLv1 && menuHasItems} hidden={hideTopNav === HIDE_TOP_NAV_OPTIONS.TRUE}>
      <TopNavWrapper hidden={hideTopNav === HIDE_TOP_NAV_OPTIONS.PRIMARY}>
        {
          !userData?.lockedOut && (
            <div className="content-btn-mobile">
              <IconButton
                icon={MenuFilled}
                onClick={(e) => onClickBtnMenu({ ...props })}
                aria-label="Mobile Menu Button"
              />
            </div>
          )
        }
        <div className="content-left">
          <div className="logo">
            {!!logoImg &&
              <a
                href="/"
                tabIndex={0}
                onKeyDown={(e) => enterKeyPress(e, null, null, MENU_TYPE.LOGO)}
                onClick={(e) => {
                  e.preventDefault();
                  onClickBtnLogo();
                }}
              >
                <UptimeImage
                  src={generateImgUrl(logoImg)}
                  alt={portalName ?? "uptime"}
                  title={portalName ?? "uptime"}
                  height={theme.layout.logoHeight ? theme.layout.logoHeight : 28}
                />
              </a>
            }
          </div>
          {homeIcon && (
            <IconButton
              icon={Home}
              color="white"
              onClick={onClickBtnLogo}
              className="home-icon"
              aria-label={globalSearchProps?.homeAccessibilityTranslation?.homeIcon}
            />
          )}
          <ClickAwayListener onClickAway={handleClickAway}>
            <ul className="list-menu">{listMenu(menuItems)}</ul>
          </ClickAwayListener>
        </div>
        <div className="content-middle">
          {
            !userData?.lockedOut && (
              <GlobalSearch {...globalSearchProps} />
            )
          }
        </div>
        <div className="content-right">
          {children}
        </div>
      </TopNavWrapper>
    </StyledTopNav>
  );
};

TopNav.defaultProps = {
  logoImg: "https://uptime-dev.azurefd.net/cdn/static-image/logo.svg",
  menuItems:[],
  location: { pathname: '' },
  onClickBtnMenu: () => {},
  onClickBtnLogo: () => {},
  onItemClick: () => {},
  handleOpenSubmenu : () => {},
};

export default memo(TopNav);

const TopNavWrapper = ({ children, hidden }: { children: React.ReactNode, hidden: boolean }) => {
  return (
    <StyledTopNavWrapper hidden={hidden}>
      <TopNavWrapperContainer id='dv-topnav-wrapper_container'>
        {children}
      </TopNavWrapperContainer>
    </StyledTopNavWrapper>
  );
};
